import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    PartnerDetailed,
    PartnerSliceModel,
    GetPartnerResponse,
    GetPartnersResponse,
    PartnerBasicInfoTableResponse,
    DeletePartnerReponse,
    CreatePartnerBasicInfoRequest,
    CreatePartnerResponse,
    ActivatePartnerResponse,
    DeactivatePartnerResponse,
    CreatePartnerDataResponse,
    BankDataRequest,
    CreateBankdataResponse,
    UpdateBankdataResponse,
    GetPartnerLocationsResponse,
    PartnerLocationRequest,
    UpdateLocationResponse,
    CreateLocationResponse,
    BankData,
    initialCreatePartnerValue,
    DeletePartnerLocationReponse,
    GetPartnerDraftsResponse,
    GetPartnerDraftResponse,
    PartnerDraft,
    ConfirmPartnerDraftRequest,
    ConfirmPartnerDraftResponse,
    DiscardPartnerDraftResponse
} from './partner-interfaces'
import { RejectedValue, ErrorResponse, ImportHubspotListResponse, FilteredDropDownModelInit, FilteredDropDownResponse, FilteredDropDownRequest, ListModel, TableQueryParams, MainResponse } from "shared/interfaces/core-interfaces";
import { AuthHeader } from "services/authHeader";
import { Guid } from "guid-typescript";
import { SetPropValue } from "../../../Finance/FinanceDocuments/state/finance-document-interfaces";
import { API_BASE_URL as baseUri } from "config/env";

const initialValue: PartnerSliceModel = {
    statusCode: '',
    isLoading: false,
    isModalLoading: false,
    isModalSubmited: false,
    message: '',
    showNotification: false,
    activePartners: [],
    inactivePartners: [],
    draftPartners: [],
    partnerTypes: [],
    industrySectors: [],
    partner: null,
    partnerToShow: null,
    partnerBasicInfo: null,
    partnerBankData: [],
    partnerLocations: [],
    bankDataForUpdate: null,
    locationForUpdate: null,
    createPartnerData: {
        partnerTypes: [],
        industrySectors: [],
        articles: [],
        employees: [],
        countries: [],
        banks: [],
        bankContacts: {},
        activePartnersList: [],
        timeZones: [],
        lifecycleStages: [],
        leadStatuses: []
    },
    reloadBankData: false,
    reloadLocation: false,
    reloadData: false,
    importPartners: {
        importedPartnersResponse: null,
        isLoading: false
    },
    filteredPartnersList: FilteredDropDownModelInit,
    activePartnersCount: 0,
    inactivePartnersCount: 0
}

// GET ACTIVE PARTNERS
/**
 * Plain (non-thunk) fetchers used by Excel export. They deliberately bypass the slice so that
 * pulling every row for the export does not disturb the page currently on screen.
 */
async function fetchAllPartners(url: string, params: TableQueryParams): Promise<PartnerBasicInfoTableResponse[]> {
    const response = await fetch(baseUri + url, {
        method: "POST",
        headers: AuthHeader(),
        body: JSON.stringify(params)
    });
    if (response.status !== 200) return [];
    const responseModel = await response.json() as GetPartnersResponse;
    return responseModel.partners ?? [];
}

export const fetchAllActivePartnersForExport = (params: TableQueryParams) => fetchAllPartners('api/partners', params);
export const fetchAllInactivePartnersForExport = (params: TableQueryParams) => fetchAllPartners('api/partners/inactive', params);

export const GetActivePartners = createAsyncThunk<GetPartnersResponse, TableQueryParams, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetActivePartners',
    async (params: TableQueryParams, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(params)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPartnersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET INACTIVE PARTNERS
export const GetInactivePartners = createAsyncThunk<GetPartnersResponse, TableQueryParams, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetInactivePartners',
    async (params: TableQueryParams, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/inactive', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(params)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPartnersResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: errorResponse.status });
        }
    }
);

// GET PARTNER DRAFTS
// All of the corporation's drafts at once, newest first; the list is filtered and paged on screen.
export const GetPartnerDrafts = createAsyncThunk<GetPartnerDraftsResponse, void, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetPartnerDrafts',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drafts', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPartnerDraftsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET PARTNER DRAFT BY ID
// The details page keeps the draft in its own state, so this thunk has no reducers.
export const GetPartnerDraft = createAsyncThunk<PartnerDraft, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetPartnerDraft',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drafts/get?partnerId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPartnerDraftResponse;
            return responseModel.draft;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// CONFIRM PARTNER DRAFT
// Turns the draft into a regular company; the response holds the new company's id, which is the
// draft's id. The caller shows the result, the reducer only takes the draft off the list.
export const ConfirmPartnerDraft = createAsyncThunk<ConfirmPartnerDraftResponse, ConfirmPartnerDraftRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/ConfirmPartnerDraft',
    async (request: ConfirmPartnerDraftRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drafts/confirm', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ConfirmPartnerDraftResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// DISCARD PARTNER DRAFT
// The caller shows the result, the reducer only takes the draft off the list.
export const DiscardPartnerDraft = createAsyncThunk<DiscardPartnerDraftResponse, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/DiscardPartnerDraft',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drafts/discard?partnerId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DiscardPartnerDraftResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET PARTNER BY ID
export const GetPartner = createAsyncThunk<PartnerDetailed, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetPartner',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/get?partnerId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as GetPartnerResponse;
            return responseModel.partner;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// CREATE PARTNER
export const CreatePartner = createAsyncThunk<CreatePartnerResponse, CreatePartnerBasicInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/CreatePartner',
    async (partner: CreatePartnerBasicInfoRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(partner)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePartnerResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// ACTIVATE PARTNER
export const ActivatePartner = createAsyncThunk<ActivatePartnerResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/ActivatePartner',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/activate?partnerId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as ActivatePartnerResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// DEACTIVATE PARTNER
export const DeactivatePartner = createAsyncThunk<DeactivatePartnerResponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/DeactivatePartner',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + `api/partners/deactivate?partnerId=` + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DeactivatePartnerResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// DELETE PARTNER
export const DeletePartner = createAsyncThunk<DeletePartnerReponse, Guid, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/DeletePartner',
    async (id: Guid, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/delete?partnerId=' + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as DeletePartnerReponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

// GET CREATE PARTNER DATA
export const GetCreatePartnerdata = createAsyncThunk<CreatePartnerDataResponse, void, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetCreatePartnerdata',
    async (_, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/get-create-data', {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePartnerDataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// BASIC INFO THUNKS***************************************************************************
// GET BASIC INFO
export const GetPartnerBasicInfo = createAsyncThunk<CreatePartnerBasicInfoRequest, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetPartnerBasicInfo',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/get-basicinfo-for-update?partnerId=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePartnerBasicInfoRequest;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// UPDATE BASIC INFO
export const UpdatePartnerBasicInfo = createAsyncThunk<CreatePartnerResponse, CreatePartnerBasicInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/UpdatePartnerBasicInfo',
    async (request: CreatePartnerBasicInfoRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/update', {
            method: "PUT",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePartnerResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// CREATE BASIC INFO
export const CreatePartnerBasicInfo = createAsyncThunk<CreatePartnerResponse, CreatePartnerBasicInfoRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/CreatePartnerBasicInfo',
    async (request: CreatePartnerBasicInfoRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/create', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(request)
        });
        if (response.status === 200) {
            const responseModel = await response.json() as CreatePartnerResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// BANK DATA THUNKS****************************************************************************
// CREATE BANK DATA
export const CreateBankData = createAsyncThunk<CreateBankdataResponse, BankDataRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/CreateBankData',
    async (request: BankDataRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bankdata/create', {
            body: JSON.stringify(request),
            method: "POST",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as CreateBankdataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// UPDATE BANK DATA
export const UpdateBankData = createAsyncThunk<UpdateBankdataResponse, BankDataRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/UpdateBankData',
    async (request: BankDataRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bankdata/update', {
            body: JSON.stringify(request),
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as CreateBankdataResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// GET BANK DATA BY ID
export const GetBankDataById = createAsyncThunk<BankDataRequest, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetBankDataById',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bankdata/get-for-update?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as BankDataRequest;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// GET BANK DATA BY PARTNER ID
export const GetBankData = createAsyncThunk<BankData[], string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetBankData',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/bankdata/by-partner?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as BankData[];
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)


export const DeletePartnerBankData = createAsyncThunk<MainResponse, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/DeletePartnerBankData',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + "api/bankdata/delete?id=" + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.ok) {
            const successResponse = await response.json() as MainResponse;
            return successResponse;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// PARTNER LOCATIONS*******************************************************************
// GET LOCATIONS BY PARTNER ID
export const GetLocations = createAsyncThunk<GetPartnerLocationsResponse, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetLocations',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-location/by-partner?id=' + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as GetPartnerLocationsResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// GET LOCATION BY ID
export const GetLocationById = createAsyncThunk<PartnerLocationRequest, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetLocationById',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + "api/partner-location/get-for-update?id=" + id, {
            method: "GET",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as PartnerLocationRequest;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// UPDATE LOCATION
export const UpdateLocation = createAsyncThunk<UpdateLocationResponse, PartnerLocationRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/UpdateLocation',
    async (request: PartnerLocationRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-location/update', {
            body: JSON.stringify(request),
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as UpdateLocationResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// CRATE LOCATION
export const CreateLocation = createAsyncThunk<CreateLocationResponse, PartnerLocationRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/CreateLocation',
    async (request: PartnerLocationRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partner-location/create', {
            body: JSON.stringify(request),
            method: "POST",
            headers: AuthHeader()
        });
        if (response.ok) {
            const responseModel = await response.json() as CreateLocationResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const DeletePartnerLocation = createAsyncThunk<DeletePartnerLocationReponse, string, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/DeletePartnerLocation',
    async (id: string, { rejectWithValue }) => {
        const response = await fetch(baseUri + "api/partner-location/delete?id=" + id, {
            method: "PUT",
            headers: AuthHeader()
        });
        if (response.ok) {
            const successResponse = await response.json() as DeletePartnerLocationReponse;
            return successResponse;
        } else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

// Import Hubspot Partners
export const ImportHubspotPartners = createAsyncThunk<ImportHubspotListResponse, FormData, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/ImportHubspotPartners',
    async (request: FormData, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/import/hubspot', {
            body: request,
            method: "POST",
            headers: AuthHeader(false)
        });
        if (response.ok) {
            const responseModel = await response.json() as ImportHubspotListResponse;
            return responseModel;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
)

export const GetPartnersForDropdown = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetPartnersForDropdown',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const GetPartnersForSuggestions = createAsyncThunk<FilteredDropDownResponse, FilteredDropDownRequest, { rejectValue: RejectedValue }>(
    'Slice/PartnersSlice/GetPartnersForSuggestions',
    async (data: FilteredDropDownRequest, { rejectWithValue }) => {
        const response = await fetch(baseUri + 'api/partners/drop-down', {
            method: "POST",
            headers: AuthHeader(),
            body: JSON.stringify(data)
        });
        if (response.status === 200) {
            const getResponse = await response.json() as FilteredDropDownResponse;
            return getResponse;
        }
        else {
            const errorResponse = await response.json() as ErrorResponse;
            return rejectWithValue({ message: errorResponse.detail, status: response.status });
        }
    }
);

export const PartnersSlice = createSlice({
    name: 'PartnersSlice',
    initialState: initialValue,
    reducers: {
        resetPartnersState(state) {
            state.statusCode = '';
            state.isLoading = false;
            state.isModalLoading = false;
            state.isModalSubmited = false;
            state.message = '';
            state.showNotification = false;
            state.activePartners = [];
            state.inactivePartners = [];
            state.draftPartners = [];
            state.partnerTypes = [];
            state.industrySectors = [];
            state.partner = null;
            state.partnerToShow = null;
            state.createPartnerData = {
                partnerTypes: [],
                industrySectors: [],
                articles: [],
                employees: [],
                countries: [],
                banks: [],
                bankContacts: {},
                activePartnersList: [],
                timeZones: [],
                lifecycleStages: [],
                leadStatuses: []
            };
            state.locationForUpdate = null;
            state.bankDataForUpdate = null;
            state.partnerLocations = [];
            state.partnerBankData = [];
            state.reloadBankData = false;
            state.reloadLocation = false;
            state.partnerBasicInfo = null;
            state.importPartners = {
                importedPartnersResponse: null,
                isLoading: false
            };
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        resetSubmitModalValue: (state) => {
            state.isModalSubmited = false;
        },
        resetPartnerState: (state) => {
            state.partner = null;
            state.partnerToShow = null;
            state.locationForUpdate = null;
            state.bankDataForUpdate = null;
            state.partnerLocations = [];
            state.partnerBankData = [];
            state.reloadBankData = false;
            state.reloadLocation = false;
            state.partnerBasicInfo = initialCreatePartnerValue;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        resetStatusCode: (state) => {
            state.statusCode = '';
            state.message = '';
            state.showNotification = false;
            state.reloadData = false;
        },
        resetCreatepartnerData: (state) => {
            state.createPartnerData = {
                partnerTypes: [],
                industrySectors: [],
                articles: [],
                employees: [],
                countries: [],
                banks: [],
                bankContacts: {},
                activePartnersList: [],
                timeZones: [],
                lifecycleStages: [],
                leadStatuses: []
            };
            state.isModalLoading = false;
        },
        resetReloadBankData: (state) => {
            state.reloadBankData = false;
        },
        resetReloadLocations: (state) => {
            state.reloadLocation = false;
        },
        resetLocationForUpdate: (state) => {
            state.locationForUpdate = null;
        },
        resetBankDataForUpdate: (state) => {
            state.bankDataForUpdate = null;
        },
        resetPartnerData: (state) => {
            state.partner = null;
            state.partnerToShow = null;
            state.filteredPartnersList = FilteredDropDownModelInit;
        },
        setPlaceOfJuridiction: (state, action: PayloadAction<string>) => {
            if (state.partnerBasicInfo !== null) {
                state.partnerBasicInfo.placeOfJurisdiction = action.payload;
            }
        },
        resetPartnersImport: (state) => {
            state.importPartners = {
                importedPartnersResponse: null,
                isLoading: false
            }
        },
        // Fills the company form from somewhere other than a saved company, e.g. a draft being confirmed.
        setPartnerBasicInfo: (state, action: PayloadAction<CreatePartnerBasicInfoRequest>) => {
            state.partnerBasicInfo = action.payload;
        },
        setPartnerBasicInfoProp: (state, action: PayloadAction<SetPropValue>) => {
            if (state.partnerBasicInfo === null) {
                state.partnerBasicInfo = initialCreatePartnerValue;
            }
            switch (action.payload.propName) {
                case 'childCompanies':
                    state.partnerBasicInfo.childCompanies = action.payload.value as ListModel<string>[];
                    break;
                case 'parentCompanies':
                    state.partnerBasicInfo.parentCompanies = action.payload.value;
                    break;
                case 'partnerTypes':
                    state.partnerBasicInfo.partnerTypes = action.payload.value;
                    break;
                case 'industrySectors':
                    state.partnerBasicInfo.industrySectors = action.payload.value;
                    break;
                case 'articlesForFront':
                    state.partnerBasicInfo.articlesForFront = action.payload.value;
                    break;
                case 'articles':
                    state.partnerBasicInfo.articles = action.payload.value;
                    break;
                case 'partnerOwner':
                    state.partnerBasicInfo.partnerOwner = action.payload.value;
                    break;
                case 'name':
                    state.partnerBasicInfo.name = action.payload.value;
                    break;
                case 'abbreviation':
                    state.partnerBasicInfo.abbreviation = action.payload.value;
                    break;
                case 'phone':
                    state.partnerBasicInfo.phone = action.payload.value;
                    break;
                case 'fax':
                    state.partnerBasicInfo.fax = action.payload.value;
                    break;
                case 'email':
                    state.partnerBasicInfo.email = action.payload.value;
                    break;
                case 'invoiceEmail':
                    state.partnerBasicInfo.invoiceEmail = action.payload.value;
                    break;
                case 'website':
                    state.partnerBasicInfo.website = action.payload.value;
                    break;
                case 'uid':
                    state.partnerBasicInfo.uid = action.payload.value;
                    break;
                case 'registerNumber':
                    state.partnerBasicInfo.registerNumber = action.payload.value;
                    break;
                case 'taxNumber':
                    state.partnerBasicInfo.taxNumber = action.payload.value;
                    break;
                case 'linkedInPage':
                    state.partnerBasicInfo.linkedInPage = action.payload.value;
                    break;
                case 'description':
                    state.partnerBasicInfo.description = action.payload.value;
                    break;
                case 'annualRevenue':
                    state.partnerBasicInfo.annualRevenue = action.payload.value;
                    break;
                case 'numberOfEmployees':
                    state.partnerBasicInfo.numberOfEmployees = action.payload.value;
                    break;
                case 'placeOfJurisdiction':
                    state.partnerBasicInfo.placeOfJurisdiction = action.payload.value;
                    break;
                case 'hasLocations':
                    state.partnerBasicInfo.hasLocations = action.payload.value;
                    break;
                case 'hasBankData':
                    state.partnerBasicInfo.hasBankData = action.payload.value;
                    break;
                case 'clientNumber':
                    state.partnerBasicInfo.clientNumber = action.payload.value;
                    break;
                case 'supplierNumber':
                    state.partnerBasicInfo.supplierNumber = action.payload.value;
                    break;
                case 'logoURL':
                    state.partnerBasicInfo.logoURL = action.payload.value;
                    break;
                case 'timeZone':
                    state.partnerBasicInfo.timeZone = action.payload.value;
                    break;
                case 'yearFounded':
                    state.partnerBasicInfo.yearFounded = action.payload.value;
                    break;
                case 'facebookCompanyPage':
                    state.partnerBasicInfo.facebookCompanyPage = action.payload.value;
                    break;
                case 'lifecycleStage':
                    state.partnerBasicInfo.lifecycleStage = action.payload.value;
                    break;
                case 'leadStatus':
                    state.partnerBasicInfo.leadStatus = action.payload.value;
                    break;

                default:
                    console.log("Missing prop name: " + action.payload.propName)
                    break;
            }
        },
    },
    extraReducers(builder) {
        // Get active partners.
        builder.addCase(GetActivePartners.fulfilled, (state, action: PayloadAction<GetPartnersResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.activePartnersCount = action.payload.count;
            state.activePartners = action.payload.partners;
            state.createPartnerData.activePartnersList = action.payload.partners.map(partner => ({
                value: partner.id!.toString(),
                label: partner.name
            }));
        })
            .addCase(GetActivePartners.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetActivePartners.pending, state => {
                // state.isLoading = true;
            })

        // Get inactive partners.
        builder.addCase(GetInactivePartners.fulfilled, (state, action: PayloadAction<GetPartnersResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.inactivePartnersCount = action.payload.count;
            state.inactivePartners = action.payload.partners;
        })
            .addCase(GetInactivePartners.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetInactivePartners.pending, state => {
                // state.isLoading = true;
            })

        // Get partner drafts.
        builder.addCase(GetPartnerDrafts.fulfilled, (state, action: PayloadAction<GetPartnerDraftsResponse>) => {
            state.isLoading = false;
            state.message = '';
            state.draftPartners = action.payload?.drafts ?? [];
        })
            .addCase(GetPartnerDrafts.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })

        // A confirmed or discarded draft is no longer a draft.
        builder.addCase(ConfirmPartnerDraft.fulfilled, (state, action) => {
            state.draftPartners = state.draftPartners.filter(draft => draft.id !== action.meta.arg.id);
        })
        builder.addCase(DiscardPartnerDraft.fulfilled, (state, action) => {
            state.draftPartners = state.draftPartners.filter(draft => draft.id !== action.meta.arg);
        })

        // Get partner by ID
        builder.addCase(GetPartner.fulfilled, (state, action: PayloadAction<PartnerDetailed>) => {
            state.isLoading = false;
            state.message = '';
            state.partner = action.payload;
            state.partnerToShow = action.payload;
        })
            .addCase(GetPartner.rejected, (state, action) => {
                state.isLoading = false;
                state.message = action.payload?.message ?? "Error in application";
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid.';
            })
            .addCase(GetPartner.pending, state => {
                state.isLoading = true;
            })

        // Activate partner
        builder.addCase(ActivatePartner.fulfilled, (state, action: PayloadAction<ActivatePartnerResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.isModalLoading = false;
            state.isModalSubmited = true;
            state.showNotification = true;
            state.statusCode = '200';
            state.reloadData = true;
        })
            .addCase(ActivatePartner.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ActivatePartner.pending, state => {
                state.isLoading = true;
            })

        // Deactivate partner
        builder.addCase(DeactivatePartner.fulfilled, (state, action: PayloadAction<DeactivatePartnerResponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.isModalLoading = false;
            state.isModalSubmited = true;
            state.showNotification = true;
            state.statusCode = '200';
            state.reloadData = true;
            state.activePartnersCount = state.activePartnersCount - 1;
            state.activePartners = state.activePartners.filter(partner => partner.id !== action.payload.id);
        })
            .addCase(DeactivatePartner.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeactivatePartner.pending, state => {
                state.isLoading = true;
            })

        // Delete partner
        builder.addCase(DeletePartner.fulfilled, (state, action: PayloadAction<DeletePartnerReponse>) => {
            state.isLoading = false;
            state.message = action.payload.message;
            state.reloadData = true;
            state.isModalLoading = false;
            state.isModalSubmited = true;
            state.showNotification = true;
            state.statusCode = '200';
        })
            .addCase(DeletePartner.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeletePartner.pending, state => {
                state.isLoading = true;
            })

        // GET CREATE DATA
        builder.addCase(GetCreatePartnerdata.fulfilled, (state, action: PayloadAction<CreatePartnerDataResponse>) => {
            state.isModalLoading = false;
            state.createPartnerData = {
                ...state.createPartnerData,
                ...action.payload,
            };
            state.message = '';
            state.statusCode = '';
        })
            .addCase(GetCreatePartnerdata.rejected, (state, action) => {
                state.isModalLoading = false;
                state.isModalSubmited = true;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetCreatePartnerdata.pending, state => {
                state.isModalLoading = true;
            })

        // PARTNER BASIC INFO REDUCERS**********************************************
        // GET BASIC INFO
        builder.addCase(GetPartnerBasicInfo.fulfilled, (state, action: PayloadAction<CreatePartnerBasicInfoRequest>) => {
            state.isModalLoading = false;
            state.partnerBasicInfo = action.payload;
        })
            .addCase(GetPartnerBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetPartnerBasicInfo.pending, state => {
                state.isModalLoading = true;
            })

        // CREATE BASIC INFO
        builder.addCase(CreatePartnerBasicInfo.fulfilled, (state, action) => {
            state.isModalLoading = false;
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = '200';
            state.partnerBasicInfo = { ...action.meta.arg, id: action.payload.id };
        })
            .addCase(CreatePartnerBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            }).addCase(CreatePartnerBasicInfo.pending, state => {
                // state.isModalLoading = true;
            })

        // UPDATE BASIC INFO
        builder.addCase(UpdatePartnerBasicInfo.fulfilled, (state, action: PayloadAction<CreatePartnerResponse>) => {
            state.isModalLoading = false;
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = '200';
        })
            .addCase(UpdatePartnerBasicInfo.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            }).addCase(UpdatePartnerBasicInfo.pending, state => {
                // state.isModalLoading = true;
            })

        // PARTNER BANK DATA REDUCERS**********************************************
        // CREATE BANK DATA
        builder.addCase(CreateBankData.fulfilled, (state, action: PayloadAction<CreateBankdataResponse>) => {
            state.isModalLoading = false;
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = '200';
            state.reloadBankData = true;
        })
            .addCase(CreateBankData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateBankData.pending, state => {
                state.isModalLoading = true;
            })

        // UPDATE BANK DATA
        builder.addCase(UpdateBankData.fulfilled, (state, action: PayloadAction<UpdateBankdataResponse>) => {
            state.isModalLoading = false;
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = '200';
            state.reloadBankData = true;
        })
            .addCase(UpdateBankData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateBankData.pending, state => {
                state.isModalLoading = true;
            })

        // GET BANK DATA BY ID
        builder.addCase(GetBankDataById.fulfilled, (state, action: PayloadAction<BankDataRequest>) => {
            state.isModalLoading = false;
            state.bankDataForUpdate = action.payload;
        })
            .addCase(GetBankDataById.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetBankDataById.pending, state => {
                state.isModalLoading = true;
            })

        // GET BANK DATA
        builder.addCase(GetBankData.fulfilled, (state, action: PayloadAction<BankData[]>) => {
            state.isModalLoading = false;
            state.partnerBankData = action.payload;
        })
            .addCase(GetBankData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetBankData.pending, state => {
                state.isModalLoading = true;
            })


        // DELETE PARTNER BANK DATA
        builder
            .addCase(DeletePartnerBankData.fulfilled, (state, action: PayloadAction<MainResponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.showNotification = true;
                state.statusCode = "200";
            })
            .addCase(DeletePartnerBankData.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeletePartnerBankData.pending, state => {
                state.isModalLoading = true;
            })


        // PARTNER LOCATION REDUCERS**************************************
        // CREATE PARTNER LOCATION
        builder.addCase(CreateLocation.fulfilled, (state, action: PayloadAction<CreateLocationResponse>) => {
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = '200';
            state.reloadLocation = true;
        })
            .addCase(CreateLocation.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(CreateLocation.pending, state => {
            })

        // UPDATE PARTNER LOCATION
        builder.addCase(UpdateLocation.fulfilled, (state, action: PayloadAction<UpdateLocationResponse>) => {
            state.message = action.payload.message;
            state.showNotification = true;
            state.statusCode = '200';
            state.reloadLocation = true;
        })
            .addCase(UpdateLocation.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(UpdateLocation.pending, state => {
            })

        // GET LOCATION BY ID
        builder.addCase(GetLocationById.fulfilled, (state, actions: PayloadAction<PartnerLocationRequest>) => {
            state.locationForUpdate = actions.payload;
        })
            .addCase(GetLocationById.rejected, (state, action) => {
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetLocationById.pending, state => {
            })

        // GET LOCATIONS
        builder.addCase(GetLocations.fulfilled, (state, action: PayloadAction<GetPartnerLocationsResponse>) => {
            state.isModalLoading = false;
            state.partnerLocations = action.payload.locations;
            if (state.partnerBasicInfo !== null) {
                state.partnerBasicInfo.placeOfJurisdiction = action.payload.locations.find(l => l.isPrimary === true)?.city!;
            }
        })
            .addCase(GetLocations.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(GetLocations.pending, state => {
                state.isModalLoading = true;
            })

        // DELETE PARTNER LOCATION
        builder
            .addCase(DeletePartnerLocation.fulfilled, (state, action: PayloadAction<DeletePartnerLocationReponse>) => {
                state.isModalLoading = false;
                state.message = action.payload.message;
                state.statusCode = "200";
                state.showNotification = true;
                state.reloadLocation = true;

            })
            .addCase(DeletePartnerLocation.rejected, (state, action) => {
                state.isModalLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(DeletePartnerLocation.pending, state => {
                state.isModalLoading = true;
            })

        // Import Hubspot Partners
        builder
            .addCase(ImportHubspotPartners.fulfilled, (state, action: PayloadAction<ImportHubspotListResponse>) => {
                state.importPartners.isLoading = false;
                state.importPartners.importedPartnersResponse = action.payload;
                state.message = action.payload.message;
                state.statusCode = "200";
            })
            .addCase(ImportHubspotPartners.rejected, (state, action) => {
                state.importPartners.isLoading = false;
                state.message = action.payload?.message ?? 'Error in application';
                state.showNotification = true;
                state.statusCode = action.payload?.status?.toString() ?? 'Code is not valid';
            })
            .addCase(ImportHubspotPartners.pending, state => {
                state.importPartners.isLoading = true;
            })
        //Get Partners list    
        builder.addCase(GetPartnersForDropdown.pending, (state) => {
            state.filteredPartnersList.loading = true;
            state.filteredPartnersList.error = null;
        })
            .addCase(GetPartnersForDropdown.fulfilled, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.data = action.payload.data;
                state.filteredPartnersList.totalItems = action.payload.totalItems;
                state.filteredPartnersList.totalPages = action.payload.totalPages;
                state.filteredPartnersList.currentPage = action.payload.currentPage;
            })
            .addCase(GetPartnersForDropdown.rejected, (state, action) => {
                state.filteredPartnersList.loading = false;
                state.filteredPartnersList.error = action.payload?.message || 'Error fetching data';
            });
    }
})

export const { resetPartnersState, resetSubmitModalValue, resetPartnerState,
    resetStatusCode, resetCreatepartnerData, resetReloadBankData,
    resetLocationForUpdate, resetReloadLocations, resetBankDataForUpdate, resetPartnerData,
    setPlaceOfJuridiction, resetPartnersImport, setPartnerBasicInfo, setPartnerBasicInfoProp } = PartnersSlice.actions;
export default PartnersSlice.reducer;
